import React, {useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import {Button, Tooltip} from "@mui/material";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import BeatLoaderMini from "components/loaders/BeatLoaderMini";

import {red600} from "assets/jss/main";

import {formatDateTime} from "views/personalShopper/components/formatters";

import ProductPickerDialog from "./ProductPickerDialog";

HeaderActions.propTypes = {
    conversation: PropTypes.object.isRequired,
    heldByMe: PropTypes.bool.isRequired,
    onTakeOver: PropTypes.func.isRequired,
    onRelease: PropTypes.func.isRequired,
    onSendProducts: PropTypes.func.isRequired,
};

/**
 * Who answers the shopper, and the buttons to change it: taking over silences the assistant
 * until the conversation is handed back.
 */
function HeaderActions({conversation, heldByMe, onTakeOver, onRelease, onSendProducts}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const canSendProducts = heldByMe && conversation.customerServiceWindowOpen;

    const run = (action) => {
        setBusy(true);
        setError(null);
        action()
            .catch((requestError) => setError(requestError.message))
            .finally(() => setBusy(false));
    };

    const heldByOther = !!conversation.handledBy && !heldByMe;

    return (
        <div style={{display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap"}}>
            {!conversation.handledBy && (
                <Tooltip title={<FormattedMessage id="personalShopper.handover.assistantAnswers" />}>
                    <Button variant="contained" color="warning" size="small" startIcon={<PanToolOutlinedIcon />} disabled={busy} onClick={() => run(() => onTakeOver(false))} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.handover.takeOver" />
                        <BeatLoaderMini loading={busy} />
                    </Button>
                </Tooltip>
            )}
            {heldByOther && (
                <Tooltip title={<FormattedMessage id="personalShopper.handover.otherAnswers" values={{who: conversation.handledBy, since: formatDateTime(conversation.handledSince)}} />}>
                    <Button variant="outlined" color="warning" size="small" startIcon={<PanToolOutlinedIcon />} disabled={busy} onClick={() => run(() => onTakeOver(true))} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.handover.takeOverForce" />
                    </Button>
                </Tooltip>
            )}
            {conversation.handledBy && (
                <Tooltip title={heldByMe ? <FormattedMessage id="personalShopper.handover.youAnswer" values={{since: formatDateTime(conversation.handledSince)}} /> : ""}>
                    <Button variant="outlined" size="small" startIcon={<SmartToyOutlinedIcon />} disabled={busy} onClick={() => run(onRelease)} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.handover.release" />
                    </Button>
                </Tooltip>
            )}
            <Tooltip title={<FormattedMessage id={canSendProducts ? "personalShopper.products.dialog.title" : "personalShopper.products.dialog.disabled"} />}>
                <span>
                    <Button variant="outlined" size="small" startIcon={<StorefrontOutlinedIcon />} disabled={!canSendProducts} onClick={() => setPickerOpen(true)} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.products.dialog.button" />
                    </Button>
                </span>
            </Tooltip>
            <ProductPickerDialog open={pickerOpen} onSend={onSendProducts} onClose={() => setPickerOpen(false)} />
            {error && <span style={{color: red600, fontSize: "12px"}}>{error}</span>}
        </div>
    );
}

export default HeaderActions;
